import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Plus, Edit2, Trash2, X, ShieldCheck, Users, KeyRound, Search, Link2, Loader2, RefreshCw } from 'lucide-react';

const ROLE_META = {
  admin:  { label: 'Admin',  color: 'bg-[#A3FF12]/15 text-[#A3FF12] border-[#A3FF12]/30',  desc: 'Acceso total' },
  gestor: { label: 'Gestor', color: 'bg-blue-500/15 text-blue-300 border-blue-500/30',     desc: 'Negociaciones y capitalizaciones' },
  hunter: { label: 'Hunter', color: 'bg-orange-500/15 text-orange-400 border-orange-500/30', desc: 'Prospección y emplazamientos' },
};

const Modal = ({ open, onClose, children, title }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="text-xl font-black text-white">{title}</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={22}/></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

const emptyForm = { name: '', email: '', password: '', role: 'hunter', agency_id: '' };

const Admin = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const [u, a] = await Promise.all([api.get('/admin/users'), api.get('/agencies')]);
      setUsers(u.data);
      setAgencies(a.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Error al cargar usuarios');
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setErr('');
    setModal(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: '', role: u.role, agency_id: u.agency_id || '' });
    setErr('');
    setModal(true);
  };

  const save = async () => {
    setErr(''); setLoading(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, agency_id: form.agency_id || null };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/users/${editing.id}`, payload);
      } else {
        await api.post('/admin/users', { ...form, agency_id: form.agency_id || null });
      }
      setModal(false);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const remove = async (u) => {
    if (u.id === user.id) { alert('No puedes eliminar tu propio usuario.'); return; }
    if (!window.confirm(`¿Eliminar a ${u.name}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al eliminar');
    }
  };

  const filtered = users.filter((u) => {
    if (roleFilter && u.role !== roleFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return u.name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s);
    }
    return true;
  });

  const countByRole = (r) => users.filter((u) => u.role === r).length;

  // ---------- Vincular Al Instante (link job) ----------
  const [linkStatus, setLinkStatus] = useState(null);
  const [linkStats, setLinkStats] = useState(null);
  const loadLink = async () => {
    try {
      const [s, st] = await Promise.all([
        api.get('/link/status'),
        api.get('/link/stats'),
      ]);
      setLinkStatus(s.data);
      setLinkStats(st.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadLink(); }, []);
  useEffect(() => {
    if (!linkStatus?.running) return;
    const t = setInterval(loadLink, 2000);
    return () => clearInterval(t);
  }, [linkStatus?.running]);

  const runLink = async () => {
    try {
      await api.post('/link/run');
      loadLink();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error lanzando el job');
    }
  };

  const linkPct = linkStatus?.total ? Math.min(100, Math.round((linkStatus.processed / linkStatus.total) * 100)) : 0;
  const nodesLinked = linkStats?.nodes?.linked ?? 0;
  const nodesTotal = linkStats?.nodes?.total ?? 0;
  const towersLinked = linkStats?.towers?.linked ?? 0;
  const towersTotal = linkStats?.towers?.total ?? 0;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6 lg:mb-8">
        <div>
          <div className="text-xs font-bold tracking-widest text-[#A3FF12] mb-2">ADMINISTRACIÓN</div>
          <h1 className="text-3xl lg:text-4xl font-black text-white">Usuarios y roles</h1>
          <p className="text-white/50 mt-2 text-sm lg:text-base">Crea, edita y asigna roles a tu equipo (admin / gestor / hunter).</p>
        </div>
        <button onClick={openCreate} className="inline-flex items-center gap-2 bg-[#A3FF12] text-black px-5 py-3 rounded-full font-bold hover:bg-[#b8ff3a] transition-colors text-sm lg:text-base">
          <Plus size={18}/> Nuevo usuario
        </button>
      </div>

      {/* Vincular al instante — background link job */}
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 lg:p-6 mb-6 lg:mb-8" data-testid="link-job-panel">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#A3FF12]/15 border border-[#A3FF12]/30 text-[#A3FF12] flex items-center justify-center flex-shrink-0">
              <Link2 size={20}/>
            </div>
            <div>
              <div className="text-white font-black text-lg">Vincular Al Instante</div>
              <p className="text-white/50 text-xs lg:text-sm mt-1 max-w-xl">
                Enlaza automáticamente los códigos KMZ ↔ base maestra (proximidad ≤150 m) y los guarda en la BD para lookups instantáneos. Se relanza solo cada 24 h.
              </p>
              {linkStatus?.last_run?.at && (
                <div className="text-white/40 text-[11px] mt-1">
                  Última ejecución: {new Date(linkStatus.last_run.at).toLocaleString('es-ES')}
                </div>
              )}
            </div>
          </div>
          <button onClick={runLink} disabled={linkStatus?.running} data-testid="link-run-btn"
            className="inline-flex items-center gap-2 bg-[#A3FF12] text-black px-4 py-2.5 rounded-full text-sm font-black hover:bg-[#b8ff3a] disabled:opacity-50 disabled:cursor-not-allowed">
            {linkStatus?.running ? <><Loader2 size={16} className="animate-spin"/> Vinculando…</> : <><RefreshCw size={16}/> Vincular ahora</>}
          </button>
        </div>

        {/* Progress */}
        {linkStatus?.running && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-white/60 mb-1">
              <span>Fase: <b className="text-[#A3FF12] uppercase">{linkStatus.phase}</b> · {linkStatus.processed}/{linkStatus.total}</span>
              <span>{linkPct}%</span>
            </div>
            <div className="h-2 bg-black/50 rounded-full overflow-hidden border border-white/5">
              <div className="h-full bg-gradient-to-r from-[#A3FF12] to-[#A3FF12]/40 transition-all" style={{ width: `${linkPct}%` }}/>
            </div>
          </div>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
          <div className="bg-black/40 border border-white/5 rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-widest text-white/40">Nodos vinculados</div>
            <div className="text-white font-black text-xl mt-1">{nodesLinked.toLocaleString('es-ES')} <span className="text-white/40 text-sm font-normal">/ {nodesTotal.toLocaleString('es-ES')}</span></div>
          </div>
          <div className="bg-black/40 border border-white/5 rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-widest text-white/40">Torres vinculadas</div>
            <div className="text-white font-black text-xl mt-1">{towersLinked.toLocaleString('es-ES')} <span className="text-white/40 text-sm font-normal">/ {towersTotal.toLocaleString('es-ES')}</span></div>
          </div>
          {['exact', 'prefix', 'sedra', 'proximity'].map((t) => {
            const nN = linkStats?.nodes?.by_type?.[t] || 0;
            const nT = linkStats?.towers?.by_type?.[t] || 0;
            return (
              <div key={t} className="bg-black/40 border border-white/5 rounded-xl p-3">
                <div className="text-[10px] uppercase tracking-widest text-white/40">{t}</div>
                <div className="text-white font-black text-xl mt-1">{(nN + nT).toLocaleString('es-ES')}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6 lg:mb-8">
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4 lg:p-5">
          <Users className="text-[#A3FF12] mb-2 lg:mb-3" size={20}/>
          <div className="text-2xl lg:text-3xl font-black text-white">{users.length}</div>
          <div className="text-white/60 text-xs lg:text-sm">Total usuarios</div>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4 lg:p-5">
          <ShieldCheck className="text-[#A3FF12] mb-2 lg:mb-3" size={20}/>
          <div className="text-2xl lg:text-3xl font-black text-white">{countByRole('admin')}</div>
          <div className="text-white/60 text-xs lg:text-sm">Admins</div>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4 lg:p-5">
          <KeyRound className="text-blue-400 mb-2 lg:mb-3" size={20}/>
          <div className="text-2xl lg:text-3xl font-black text-white">{countByRole('gestor')}</div>
          <div className="text-white/60 text-xs lg:text-sm">Gestores</div>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4 lg:p-5">
          <KeyRound className="text-orange-400 mb-2 lg:mb-3" size={20}/>
          <div className="text-2xl lg:text-3xl font-black text-white">{countByRole('hunter')}</div>
          <div className="text-white/60 text-xs lg:text-sm">Hunters</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex-1 min-w-[240px] relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"/>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre o email..."
            className="w-full bg-[#0a0a0a] border border-white/10 text-white rounded-xl pl-12 pr-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
        </div>
        <div className="flex gap-2">
          {['', 'admin', 'gestor', 'hunter'].map((r) => (
            <button key={r || 'all'} onClick={() => setRoleFilter(r)}
              className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors capitalize ${
                roleFilter === r ? 'bg-[#A3FF12] text-black border-[#A3FF12]' : 'border-white/10 text-white/70 hover:border-white/30'
              }`}>{r || 'Todos'}</button>
          ))}
        </div>
      </div>

      {/* Table (desktop) */}
      <div className="hidden md:block bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-white/[0.03] border-b border-white/10">
            <tr className="text-left text-xs font-bold tracking-widest text-white/50">
              <th className="px-5 py-3">USUARIO</th>
              <th className="px-5 py-3">EMAIL</th>
              <th className="px-5 py-3">ROL</th>
              <th className="px-5 py-3">ALTA</th>
              <th className="px-5 py-3 text-right">ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-white/40">No hay usuarios para mostrar.</td></tr>
            )}
            {filtered.map((u) => {
              const meta = ROLE_META[u.role] || ROLE_META.hunter;
              return (
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#A3FF12]/20 border border-[#A3FF12]/30 text-[#A3FF12] flex items-center justify-center font-black text-sm">
                        {u.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-white font-bold">{u.name}</div>
                        {u.id === user.id && <div className="text-[#A3FF12] text-xs font-bold">Tú</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-white/70 text-sm">{u.email}</td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${meta.color}`}>{meta.label}</span>
                  </td>
                  <td className="px-5 py-4 text-white/50 text-sm">
                    {new Date(u.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => openEdit(u)} className="p-2 text-white/50 hover:text-[#A3FF12] hover:bg-white/5 rounded-lg transition-colors" title="Editar">
                        <Edit2 size={16}/>
                      </button>
                      <button onClick={() => remove(u)} disabled={u.id === user.id}
                        className="p-2 text-white/50 hover:text-red-400 hover:bg-white/5 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed" title="Eliminar">
                        <Trash2 size={16}/>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Card list (mobile) */}
      <div className="md:hidden space-y-3">
        {filtered.length === 0 && (
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-8 text-center text-white/40">No hay usuarios para mostrar.</div>
        )}
        {filtered.map((u) => {
          const meta = ROLE_META[u.role] || ROLE_META.hunter;
          return (
            <div key={u.id} className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4 flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#A3FF12]/20 border border-[#A3FF12]/30 text-[#A3FF12] flex items-center justify-center font-black flex-shrink-0">
                {u.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-bold truncate">{u.name}</span>
                  {u.id === user.id && <span className="text-[#A3FF12] text-xs font-bold">Tú</span>}
                </div>
                <div className="text-white/60 text-xs truncate">{u.email}</div>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${meta.color}`}>{meta.label}</span>
                  <span className="text-white/40 text-xs">{new Date(u.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: '2-digit' })}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button onClick={() => openEdit(u)} className="p-2 text-white/60 hover:text-[#A3FF12] hover:bg-white/5 rounded-lg transition-colors">
                  <Edit2 size={16}/>
                </button>
                <button onClick={() => remove(u)} disabled={u.id === user.id}
                  className="p-2 text-white/60 hover:text-red-400 hover:bg-white/5 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                  <Trash2 size={16}/>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? `Editar ${editing.name}` : 'Nuevo usuario'}>
        <div className="space-y-4">
          <div>
            <label className="text-white/80 text-sm font-semibold">Nombre completo</label>
            <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
              className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]" placeholder="Juan Pérez"/>
          </div>
          <div>
            <label className="text-white/80 text-sm font-semibold">Email</label>
            <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} disabled={!!editing}
              className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12] disabled:opacity-50" placeholder="juan@empresa.com"/>
            {editing && <p className="text-white/40 text-xs mt-1">El email no se puede modificar.</p>}
          </div>
          <div>
            <label className="text-white/80 text-sm font-semibold">
              {editing ? 'Nueva contraseña' : 'Contraseña'}
              {editing && <span className="text-white/40 text-xs ml-2">(vacío = no cambiar)</span>}
            </label>
            <input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}
              minLength={editing ? 0 : 6}
              className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]" placeholder={editing ? 'Dejar vacío para mantener' : 'Mínimo 6 caracteres'}/>
          </div>
          <div>
            <label className="text-white/80 text-sm font-semibold">Rol</label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {['admin', 'gestor', 'hunter'].map((r) => {
                const meta = ROLE_META[r];
                return (
                  <button key={r} onClick={() => setForm({...form, role: r})}
                    className={`py-3 rounded-xl text-sm font-bold border transition-colors capitalize ${
                      form.role === r ? 'bg-[#A3FF12] text-black border-[#A3FF12]' : 'border-white/10 text-white/70 hover:border-white/30'
                    }`}>{meta.label}</button>
                );
              })}
            </div>
            <p className="text-white/40 text-xs mt-2">{ROLE_META[form.role]?.desc}</p>
          </div>
          {err && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg px-3 py-2">{err}</div>}
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setModal(false)} className="px-5 py-2.5 text-white/70 hover:text-white">Cancelar</button>
            <button onClick={save} disabled={loading || !form.name || !form.email || (!editing && !form.password)}
              className="bg-[#A3FF12] text-black px-6 py-2.5 rounded-xl font-bold hover:bg-[#b8ff3a] transition-colors disabled:opacity-50">
              {editing ? 'Guardar cambios' : 'Crear usuario'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Admin;
